import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Row, Typography } from "antd";
import { saveAs } from "file-saver";
import { Link } from "react-router-dom";

import CustomSelect from "../../../components/SelectableDropdown/CustomSelect/CustomSelect";
import CustomTableWithPagination from "../../../components/CustomTableWithPagination/CustomTableWithPagination";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import NoDataFound from "../../../components/EmptyTable/NoDataFound";
import { dateFormat } from "../../../commonFuntions/CommonUtilFunctions";
import {
  useLazyGetDownloadUTMAttributionReportQuery,
  useLazyGetUTMAttributionReportQuery,
} from "../../../apis/reports";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../NotificationToast/NotificationToast";
import { baseUrl } from "../../../commonUtils/commonUtils";

const { Text } = Typography;

const renderAdminLink = (value, path) => {
  if (!value) return <Text>-</Text>;

  return (
    <Link
      to={`${path}/${encodeURIComponent(value)}`}
      target="_blank"
      style={{ fontWeight: 500, color: "#2973f0", cursor: "pointer" }}
    >
      {value}
    </Link>
  );
};

const preferredColumns = [
  {
    title: "Conversion",
    dataIndex: "conversion_type",
    width: 145,
    render: (value) => <Text>{value ? formatTitle(value) : "-"}</Text>,
  },
  {
    title: "Customer",
    dataIndex: ["customer", "name"],
    width: 180,
  },
  {
    title: "Phone Number",
    dataIndex: ["customer", "phone_number"],
    width: 150,
    render: (value) =>
      renderAdminLink(value, "/admin/customer/customerDetails"),
  },
  {
    title: "Email",
    dataIndex: ["customer", "email"],
    width: 210,
  },
  {
    title: "Cart ID",
    dataIndex: "cart_id",
    width: 200,
    render: (value) => renderAdminLink(value, "/admin/customer/abandoned-carts"),
  },
  {
    title: "RFQ ID",
    dataIndex: "rfq_id",
    width: 180,
    render: (value) => renderAdminLink(value, "/admin/rfq/quotes"),
  },
  { title: "Order ID", dataIndex: "order_id", width: 220 },
  {
    title: "Value",
    dataIndex: "value",
    width: 130,
    render: (value, record) => (
      <Text>
        {value ? `${record?.currency || "INR"} ${Number(value).toFixed(2)}` : "-"}
      </Text>
    ),
  },
  {
    title: "First Source",
    dataIndex: ["first_touch", "utm_source"],
    width: 135,
  },
  {
    title: "First Medium",
    dataIndex: ["first_touch", "utm_medium"],
    width: 135,
  },
  {
    title: "First Campaign",
    dataIndex: ["first_touch", "utm_campaign"],
    width: 180,
  },
  {
    title: "First Landing Page",
    dataIndex: ["first_touch", "landing_page"],
    width: 260,
    isUrl: true,
    isLink: true,
  },
  {
    title: "Last Source",
    dataIndex: ["last_touch", "utm_source"],
    width: 135,
  },
  {
    title: "Last Medium",
    dataIndex: ["last_touch", "utm_medium"],
    width: 135,
  },
  {
    title: "Last Campaign",
    dataIndex: ["last_touch", "utm_campaign"],
    width: 180,
  },
  {
    title: "Last Landing Page",
    dataIndex: ["last_touch", "landing_page"],
    width: 260,
    isUrl: true,
    isLink: true,
  },
  { title: "Created At", dataIndex: "created_at", width: 170, isDate: true },
];

const hiddenDynamicKeys = new Set([
  "id",
  "key",
  "customer",
  "first_touch",
  "last_touch",
  "currency",
]);

const formatTitle = (key) =>
  key
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getDisplayValue = (value, column = {}) => {
  if (value === null || value === undefined || value === "") return "-";
  if (column.isDate) return dateFormat(value);
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") {
    return (
      value.name ||
      value.full_name ||
      value.project_name ||
      value.order_id ||
      value.id ||
      JSON.stringify(value)
    );
  }
  return value;
};

const renderTableText = (value, column = {}) => {
  const displayValue = getDisplayValue(value, column);
  const textStyle = column.width
    ? {
        display: "block",
        maxWidth: column.width - 24,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }
    : undefined;

  if ((column.isLink || column.isUrl) && displayValue !== "-") {
    return (
      <a
        href={displayValue}
        target="_blank"
        rel="noreferrer"
        title={displayValue}
        style={textStyle}
      >
        {displayValue}
      </a>
    );
  }

  return (
    <Text
      title={typeof displayValue === "string" ? displayValue : undefined}
      style={textStyle}
    >
      {displayValue}
    </Text>
  );
};

const getColumnValue = (record, column) => {
  if (Array.isArray(column.dataIndex)) {
    return column.dataIndex.reduce((value, key) => value?.[key], record);
  }
  return record?.[column.dataIndex];
};

const getReportRows = (response) =>
  response?.data?.results ||
  response?.data?.data?.results ||
  response?.data?.data ||
  response?.results ||
  [];

const getPagination = (response) =>
  response?.data?.pagination || response?.data?.data?.pagination || {};

const getSummary = (response) =>
  response?.data?.summary || response?.data?.data?.summary || {};

const getFilters = (response) =>
  response?.data?.filters || response?.data?.data?.filters || {};

const getDownloadUrl = (response) =>
  response?.data?.data?.download_url ||
  response?.data?.data?.url ||
  response?.data?.download_url ||
  response?.data?.url;

const UTMReports = () => {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [getUTMReportApi, { data: utmData, isFetching: isLoading }] =
    useLazyGetUTMAttributionReportQuery();
  const [downloadUTMReportApi, { isFetching: isDownloading }] =
    useLazyGetDownloadUTMAttributionReportQuery();

  const rows = useMemo(() => {
    const reportRows = getReportRows(utmData);
    return Array.isArray(reportRows) ? reportRows : [];
  }, [utmData]);

  const pagination = getPagination(utmData);
  const summary = getSummary(utmData);
  const filters = getFilters(utmData);

  const buildReportParams = ({
    page,
    reportFilters = selectedFilters,
  } = {}) => ({
    ...(page && { page }),
    ...(reportFilters.utm_source && { utm_source: reportFilters.utm_source }),
    ...(reportFilters.utm_campaign && {
      utm_campaign: reportFilters.utm_campaign,
    }),
    ...(reportFilters.conversion_type && {
      conversion_type: reportFilters.conversion_type,
    }),
  });

  useEffect(() => {
    getUTMReportApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = (params = {}) => {
    return getUTMReportApi(buildReportParams(params));
  };

  const handleFilterChange = (key, value) => {
    const nextFilters = {
      ...selectedFilters,
      [key]: value === "clear_filter" ? undefined : value,
    };
    setSelectedFilters(nextFilters);
    setCurrentPage(1);
    getUTMReportApi(buildReportParams({
      reportFilters: nextFilters,
      page: 1,
    }));
  };

  const handlePageChange = async (currPage) => {
    setCurrentPage(currPage);
    await fetchReport({ page: currPage });
  };

  const handleExportApi = async () => {
    const response = await downloadUTMReportApi({
      ...buildReportParams(),
    });

    if (response?.status !== "fulfilled") {
      showErrorToast(
        response?.error?.data?.message || "Failed to download UTM report"
      );
      return;
    }

    const downloadUrl = getDownloadUrl(response);
    if (!downloadUrl) {
      showErrorToast("Download URL not found");
      return;
    }

    const fileUrl = /^https?:\/\//i.test(downloadUrl)
      ? downloadUrl
      : `${baseUrl}${downloadUrl}`;
    saveAs(fileUrl);
    showSuccessToast(response?.data?.message || "UTM report downloaded");
  };

  const columns = useMemo(() => {
    const rowKeys = Array.from(new Set(rows.flatMap((row) => Object.keys(row || {}))));
    const preferredKeys = new Set(
      preferredColumns
        .map((column) => column.dataIndex)
        .filter((dataIndex) => typeof dataIndex === "string")
    );
    const dynamicColumns = rowKeys
      .filter((key) => !preferredKeys.has(key) && !hiddenDynamicKeys.has(key))
      .map((key) => ({
        title: formatTitle(key),
        dataIndex: key,
        key,
        width: 160,
        ellipsis: true,
        render: (value) => renderTableText(value, { width: 160 }),
      }));

    return [
      ...preferredColumns.map((column) => ({
        ...column,
        ellipsis: true,
        key: Array.isArray(column.dataIndex)
          ? column.dataIndex.join(".")
          : column.dataIndex,
        render:
          column.render ||
          ((_, record) => (
            renderTableText(getColumnValue(record, column), column)
          )),
      })),
      ...dynamicColumns,
    ];
  }, [rows]);

  const filterOptions = useMemo(() => {
    const toOptions = (values = []) => [
      ...values.map((value) =>
        typeof value === "object"
          ? value
          : {
              value,
              label: value,
            }
      ),
      {
        value: "clear_filter",
        label: <Col style={{ color: "#006644", fontWeight: "500" }}>Clear</Col>,
      },
    ];

    return {
      utmSources: toOptions(filters?.utm_sources || []),
      utmCampaigns: toOptions(filters?.utm_campaigns || []),
      conversionTypes: toOptions(filters?.conversion_types || []),
    };
  }, [filters]);

  const summaryCards = [
    { label: "Customers", value: summary?.customers || 0 },
    { label: "Carts", value: summary?.carts || 0 },
    { label: "RFQs", value: summary?.rfqs || 0 },
    { label: "Orders", value: summary?.orders || 0 },
    {
      label: "Order Value",
      value: `₹ ${Number(summary?.order_value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    },
  ];

  return (
    <Col style={{ padding: "2rem" }}>
      <Row>
        <Col className="header-container" style={{ marginBottom: "16px" }}>
          <Text className="heading">UTM Attribution Report</Text>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginBottom: "20px", width: "100%" }}>
        {summaryCards.map((item) => (
          <Col key={item.label} xs={24} sm={12} md={8} lg={4}>
            <Col
              style={{
                backgroundColor: "#fff",
                border: "1px solid #e8e8e8",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Text style={{ color: "#5e7680", fontSize: "13px" }}>
                {item.label}
              </Text>
              <Col>
                <Text
                  style={{
                    color: "#0a243f",
                    fontSize: "20px",
                    fontWeight: 600,
                  }}
                >
                  {item.value}
                </Text>
              </Col>
            </Col>
          </Col>
        ))}
      </Row>
      <Row
        className="action-container"
        style={{ justifyContent: "space-between", gap: "16px" }}
      >
        <Col className="action-sub-container">
          <CustomSelect
            placeholder={"UTM Source"}
            width={"180px"}
            options={filterOptions.utmSources}
            handleChange={(value) => handleFilterChange("utm_source", value)}
            value={selectedFilters.utm_source}
          />
          <CustomSelect
            placeholder={"UTM Campaign"}
            width={"240px"}
            options={filterOptions.utmCampaigns}
            handleChange={(value) => handleFilterChange("utm_campaign", value)}
            value={selectedFilters.utm_campaign}
          />
          <CustomSelect
            placeholder={"Conversion Type"}
            width={"200px"}
            options={filterOptions.conversionTypes}
            handleChange={(value) => handleFilterChange("conversion_type", value)}
            value={selectedFilters.conversion_type}
          />
        </Col>
        <Col style={{ marginLeft: "auto" }}>
          <Button
            type="primary"
            className="add_customer_btn"
            onClick={handleExportApi}
            loading={isDownloading}
          >
            Export Excel
          </Button>
        </Col>
      </Row>
      <Col>
        <CustomTableWithPagination
          className="order-list-table"
          tableDataSource={rows}
          tableColumns={columns}
          locale={{
            emptyText: isLoading ? (
              <TableSkeleton length={10} />
            ) : (
              <NoDataFound text={"No UTM attribution records found!"} />
            ),
          }}
          scroll={{ x: 2600 }}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          itemsPerPage={30}
          totalEntries={!isLoading && pagination?.total_entries}
          isLoading={rows.length && isLoading}
        />
      </Col>
    </Col>
  );
};

export default UTMReports;
